import {
  PersonRef,
  V3Address,
  V3Person,
  V3PersonDetails,
  V3PersonInvitationDetails,
  VerifiedPasswordChangeRequest,
} from './people';

export interface SchoolRef {
  id: number;
}

export interface V3SchoolDetails {
  name: string;
}

export interface SchoolRefDetailed<T extends V3SchoolDetails> extends SchoolRef {
  details: T;
  oldId: string;
  labels: string;
  expDate: number;
}

export enum SchoolStatus {
  NEW = 'NEW',
  PROSPECT = 'PROSPECT',
  NON_ACCREDITED = 'NON_ACCREDITED',
  PRE_ACCREDITED = 'PRE_ACCREDITED',
  ACCREDITED = 'ACCREDITED',
  EX = 'EX',
  COLD_LEAD = 'COLD_LEAD',
  BLACKLISTED = 'BLACKLISTED',
  PRE_ACCREDITED_NO_CSPA = 'PRE_ACCREDITED_NO_CSPA',
  EXPIRED_CSPA = 'EXPIRED_CSPA',
  EXPIRED_NO_CSPA = 'EXPIRED_NO_CSPA',
  ACCREDITED_NO_CSPA = 'ACCREDITED_NO_CSPA',
}

export interface SchoolView {
}

export interface V3School
  extends SchoolRefDetailed<V3SchoolDetails>,
    SchoolView {
  status: SchoolStatus;
}

export interface CorpDetails {
  corpEntityName: string;
  corpNumber: string;
  corpDetails: string;
  corpAddress: string;
  nameOfPartners: string;
  bankReference: string;
  capital: string;
}

export interface SchoolStudentStats {
  activations: number;
  newStudents: number;
  deactivations: number;
  deletions: number;
  prolongs: number;
}

export interface SchoolStudentsEstimate {
  active: number;
  inactive: number;
  total: number;
}

export interface SchoolMemberRef {
}

export interface SchoolListView extends V3School {
  address: V3Address;
  estimate: SchoolStudentsEstimate;
  activeTeachers: number;
}

export interface StudentRef extends SchoolMemberRef {
  id: number;
}

export enum SchoolStudentStatus {
  ACTIVE = 'ACTIVE',
  REMOVED = 'REMOVED',
  INACTIVE = 'INACTIVE',
}

export interface StudentRefDetailed<T extends PersonRef> extends StudentRef {
  status: SchoolStudentStatus;
  created: string;
  modified: string;
  person: T;
  expiryDate: string;
}

export interface V3Student extends StudentRefDetailed<V3Person> {
}

export interface V3SchoolStudent extends V3Student {
  school: SchoolRef;
}

export interface ManagerRef {
  id: number;
}

export interface ManagerRefDetailed<T extends PersonRef> extends ManagerRef {
  person: T;
}

export interface V3Manager extends ManagerRefDetailed<V3Person> {
}

export interface V3SchoolManager extends V3Manager {
  school: SchoolRef;
}

export interface TeacherRef extends SchoolMemberRef {
  id: number;
}

export enum LearningUnitTeacherStatus {
  ACTIVE = 'ACTIVE',
  REMOVED = 'REMOVED',
  BLOCKED = 'BLOCKED',
}

export interface TeacherStats {
  updates: number;
  new: number;
  activations: number;
  deletions: number;
  deactivations: number;
}

export interface TeacherRefDetailed<T extends PersonRef> extends TeacherRef {
  status: LearningUnitTeacherStatus;
  person: T;
}

export interface V3Teacher extends TeacherRefDetailed<V3Person> {
}

export interface V3SchoolTeacher extends V3Teacher {
  school: SchoolRef;
}

export interface CourseRef {
  code: string;
}

export class Course implements CourseRef {
  code: string;

  static en = new Course('en')
  static enBs = new Course('enBs')
  static es = new Course('es')
  static enKid = new Course('enKids')

  constructor(code: string) {
    this.code = code
  }
}

export interface CompetenceRef {
  code: string;
}

export class Competence implements CompetenceRef {
  code: string;

  static A = new Competence('A')
  static B = new Competence('B')
  static C = new Competence('C')
  static D = new Competence('D')
  static TEST = new Competence('TEST')
  static PROVA = new Competence('PROVA')

  constructor(code: string) {
    this.code = code
  }
}

export interface CourseTeacherProfileRef<T extends CourseRef> {
  course: T;
}

export interface CourseTeacherProfileDetailsBase<T extends CompetenceRef> {
  competences: T[];
  description: string;
  accent: string;
  videoUrl: string;
}

export interface V3CourseTeacherProfileDetails
  extends CourseTeacherProfileDetailsBase<CompetenceRef> {
}

export interface CourseTeacherProfileDetails
  extends CourseTeacherProfileRef<Course>,
    CourseTeacherProfileDetailsBase<Competence> {
}

export interface V3CourseTeacherProfile
  extends CourseTeacherProfileRef<CourseRef>,
    V3CourseTeacherProfileDetails {
}

export interface V3TeacherProfile {
  language: string;
}

export enum MemberRole {
  Student = 'Student',
  Teacher = 'Teacher',
}

export interface GroupRef {
  id: number;
}

export interface GroupDetails {
  name: string;
}

export enum GroupStatus {
  ACTIVATING = 'ACTIVATING',
  ACTIVE = 'ACTIVE',
  LOCKED = 'LOCKED',
  REMOVED = 'REMOVED',
}

export interface Group extends GroupRef, GroupDetails {
  status: GroupStatus;
  createDate: string;
}

export interface GroupMemberRef {
  id: number;
}

export interface GroupMemberDetails {
  from: number;
  to: number;
  labels: string[];
}

export interface GroupMemberBase<SM extends SchoolMemberRef>
  extends GroupMemberDetails {
  member: SM;
}

export interface GroupMember<SM extends SchoolMemberRef>
  extends GroupMemberBase<SM>,
    GroupMemberRef {
}

export interface GroupTeacherRequest extends GroupMemberBase<TeacherRef> {
}

export interface GroupStudentRequest extends GroupMemberBase<StudentRef> {
}

export interface ParticipantBaseDetails extends GroupMemberDetails {
}

export interface V3GroupTeacher extends GroupMember<V3Teacher> {
}

export interface V3GroupStudent extends GroupMember<V3Student> {
}

export interface StudentsFeeRequestRef {
  id: number;
}

export interface StudentsFeeRequestDetails {
  newStudents: number;
  renewedStudents: number;
  blockedStudents: number;
  unblockedStudents: number;
  deletedStudents: number;
  totalStudentsToPayFor: number;
  dateRangeStart: string;
  dateRangeEnd: string;
  dateRangeName: string;
  qId: string;
}

export interface StudentFeePaymentConfirmationDetails {
  paymentDate: string;
  paymentOrderId: string;
}

export interface V3StudentsFeePaymentConfirmationDetails
  extends StudentFeePaymentConfirmationDetails {
  requestedBy: PersonRef;
}

export interface V3StudentsFeePaymentConfirmation
  extends V3StudentsFeePaymentConfirmationDetails {
  confirmationDate: string;
}

export interface V3StudentsFeeRequest
  extends StudentsFeeRequestRef,
    StudentsFeeRequestDetails {
  confirmation: V3StudentsFeePaymentConfirmation;
}

export interface NoteRef {
  id: number;
}

export interface V3Note extends NoteRef {
  content: NoteContent;
  createDate: string;
  modifyDate: string;
}

export interface NoteContent {
  value: string;
  type: string;
}

export interface NewsContent {
  title: string;
  subtitle: string;
  labels: string;
  copy: string;
  image: string;
}

export interface NewsRef {
  id: number;
}

export enum RequestType {
  StudentInvite = 'StudentInvite',
  TeacherInvite = 'TeacherInvite',
  ManagerInvite = 'ManagerInvite',
  PersonDetailsUpdate = 'PersonDetailsUpdate',
  StudentInstantInvite = 'StudentInstantInvite',
}

export enum ConfirmationRequestStatus {
  Requested = 'Requested',
  Confirmed = 'Confirmed',
  Revoked = 'Revoked',
  Expired = 'Expired',
  Duplicated = 'Duplicated',
  Instant = 'Instant',
}

export interface SchoolConfirmationRequestBase<T extends V3PersonDetails> {
  details: T;
  requestedEmail: string;
  expiryDate: string;
  status: ConfirmationRequestStatus;
  role: RequestType;
  requestDate: string;
}

export interface V3SchoolConfirmationRequestExtended
  extends SchoolConfirmationRequestBase<V3PersonInvitationDetails> {
  personId: number;
  schoolId: number;
}

export interface V3ManualConfirmationRequest {
  passwords: VerifiedPasswordChangeRequest;
}
